import {
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  computed,
  booleanAttribute,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { TagsComponent } from '../tags/tags.component';
import { TagDropdownValue } from '@app/framework/interfaces/input.type';

@Component({
  selector: 'app-tag-dropdown',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTooltipModule,
    DropdownComponent,
    TagsComponent,
  ],
  templateUrl: './tag-dropdown.component.html',
  styleUrls: ['./tag-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagDropdownComponent),
      multi: true,
    },
  ],
})
export class TagDropdownComponent implements ControlValueAccessor, OnInit {
  private injector = inject(Injector);

  label = input('');
  toolTip = input(
    'Select tags from the dropdown. Selected tags can be removed by clicking the "minus" icon.',
  );

  required = input(false);
  name = input('tag');

  /** All available tags that can be selected */
  availableTags = input<TagDropdownValue[]>([]);

  disabled = signal(false);
  selectedTags = signal<TagDropdownValue[]>([]);

  selectedTagArray = computed<string[]>(() => {
    return this.selectedTags().map((tag) => tag.name);
  });

  isLoading = input<boolean, string | boolean>(false, { transform: booleanAttribute });

  protected ngControl = signal<NgControl | null>(null);

  // Available tags that haven't been selected yet
  unselectedTags = computed(() => {
    const selectedTags = this.selectedTags();
    const selectedIds = selectedTags.map((tag) => tag.id);
    return this.availableTags().filter((tag) => !selectedIds.includes(tag.id));
  });

  // Placeholder text for the dropdown
  dropdownPlaceholder = computed(() => {
    return this.unselectedTags().length === 0 ? 'No more tags available' : 'Select a tag';
  });

  // Model for the dropdown (always null since we clear it after selection)
  dropdownModel = signal<TagDropdownValue | null>(null);

  private onChange: (value: TagDropdownValue[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    this.ngControl.set(this.injector.get(NgControl, null));
  }

  onTagSelected(tagId: number) {
    const tag = this.availableTags().find((tag) => tag.id === tagId);

    if (!tag) return;

    const newTags = [...this.selectedTags(), tag];
    this.selectedTags.set(newTags);

    // Notify the form that the value has changed
    this.onTouched();
    this.onChange(newTags);

    // Clear the dropdown selection
    setTimeout(() => {
      // since the dropdown component has checks in place to avoid infinite update loops
      // we need to clear the model after a short delay to ensure the dropdown value resets properly
      this.dropdownModel.set(null);
    }, 10);
  }

  removeTag(index: number) {
    const newTags = this.selectedTags().filter((_, i) => i !== index);
    this.selectedTags.set(newTags);

    // Notify the form that the value has changed
    this.onTouched();
    this.onChange(newTags);
  }

  registerOnChange(fn: (value: TagDropdownValue[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(value: TagDropdownValue[] | null): void {
    this.selectedTags.set(value && Array.isArray(value) ? value : []);
  }
}
